import { reportsApi } from '@/services/reportsApi'

const state = () => ({
  filters: {
    from: null,
    to: null,
    subject_id: null,
    group_id: null,
    pin_code: null,
    question_search: '',
  },
  loading: false,
  reportData: null,
  error: null,
  lastGeneratedReport: null,
})

const getters = {
  getFilters: (state) => state.filters,
  isLoading: (state) => state.loading,
  hasError: (state) => state.error,
  getReportData: (state) => state.reportData,
  getLastGeneratedReport: (state) => state.lastGeneratedReport,
  hasActiveFilters: (state) => {
    return Object.values(state.filters).some(
      (value) => value !== null && value !== '' && value !== undefined,
    )
  },
}

const mutations = {
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },

  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_REPORT_DATA(state, data) {
    state.reportData = data
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  CLEAR_ERROR(state) {
    state.error = null
  },

  CLEAR_FILTERS(state) {
    state.filters = {
      from: null,
      to: null,
      subject_id: null,
      group_id: null,
      pin_code: null,
      question_search: '',
    }
  },

  SET_LAST_GENERATED_REPORT(state, reportInfo) {
    state.lastGeneratedReport = reportInfo
  },
}

const actions = {
  /**
   * Fetch report with filters
   */
  async fetchReport({ commit, state }, customFilters = null) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      // Use provided filters or state filters
      const filters = customFilters || state.filters

      // Clean filters (remove null/empty values)
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== null && value !== '' && value !== undefined) {
          acc[key] = value
        }
        return acc
      }, {})

      const { from, to } = cleanFilters
      if (from && to && from === to) {
        cleanFilters.date = from
        delete cleanFilters.from
        delete cleanFilters.to
      } else if (from && !to) {
        cleanFilters.date = from
        delete cleanFilters.from
      }

      // Check if there are any filters
      if (Object.keys(cleanFilters).length === 0) {
        const error = 'Por favor selecciona al menos un filtro para generar el reporte'
        commit('SET_ERROR', error)
        return {
          success: false,
          error,
        }
      }

      const response = await reportsApi.generateSessionReport(cleanFilters)

      commit('SET_REPORT_DATA', response)
      commit('SET_LAST_GENERATED_REPORT', {
        filters: cleanFilters,
        timestamp: new Date().toISOString(),
      })

      return {
        success: true,
        data: response,
        message: 'Reporte generado exitosamente',
      }
    } catch (error) {
      let errorMessage = 'Error al generar el reporte'

      // Handle 404 (no data found)
      if (error.status === 404) {
        errorMessage = 'No se encontraron datos para los filtros especificados'
      } else if (error.status === 403) {
        errorMessage = 'No tienes permisos para generar reportes'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return {
        success: false,
        error: errorMessage,
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Download PDF report
   */
  async downloadPDF({ commit, state }, customFilters = null) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      // Use provided filters or state filters
      const filters = customFilters || state.filters

      // Clean filters (remove null/empty values)
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== null && value !== '' && value !== undefined) {
          acc[key] = value
        }
        return acc
      }, {})

      const { from, to } = cleanFilters
      if (from && to && from === to) {
        // Same day → use 'date'
        cleanFilters.date = from
        delete cleanFilters.from
        delete cleanFilters.to
      } else if (from && !to) {
        cleanFilters.date = from
        delete cleanFilters.from
      }

      // Check if there are any filters
      if (Object.keys(cleanFilters).length === 0) {
        const error = 'Por favor selecciona al menos un filtro para generar el reporte'
        commit('SET_ERROR', error)
        return {
          success: false,
          error,
        }
      }

      const blob = await reportsApi.downloadSessionReportPDF(cleanFilters)

      // Create blob URL
      const url = window.URL.createObjectURL(blob)

      // Create temporary link and click it
      const link = document.createElement('a')
      link.href = url
      link.download = `reporte-sesiones-${Date.now()}.pdf`
      document.body.appendChild(link)
      link.click()

      // Cleanup
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      commit('SET_LAST_GENERATED_REPORT', {
        filters: cleanFilters,
        timestamp: new Date().toISOString(),
      })

      return {
        success: true,
        message: 'Reporte descargado exitosamente',
      }
    } catch (error) {
      let errorMessage = 'Error al descargar el reporte'

      // Handle 404 (no data found)
      if (error.status === 404) {
        errorMessage = 'No se encontraron datos para los filtros especificados'
      } else if (error.status === 403) {
        errorMessage = 'No tienes permisos para generar reportes'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return {
        success: false,
        error: errorMessage,
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Stream PDF report (open in new tab)
   */
  async streamPDF({ commit, state }, customFilters = null) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      // Use provided filters or state filters
      const filters = customFilters || state.filters

      // Clean filters (remove null/empty values)
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== null && value !== '' && value !== undefined) {
          acc[key] = value
        }
        return acc
      }, {})

      const { from, to } = cleanFilters
      if (from && to && from === to) {
        cleanFilters.date = from
        delete cleanFilters.from
        delete cleanFilters.to
      } else if (from && !to) {
        cleanFilters.date = from
        delete cleanFilters.from
      }

      // Check if there are any filters
      if (Object.keys(cleanFilters).length === 0) {
        const error = 'Por favor selecciona al menos un filtro para generar el reporte'
        commit('SET_ERROR', error)
        return {
          success: false,
          error,
        }
      }

      const blob = await reportsApi.streamSessionReportPDF(cleanFilters)

      // Create blob URL and open in new tab
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')

      // Cleanup after a delay to allow the browser to open the file
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
      }, 1000)

      commit('SET_LAST_GENERATED_REPORT', {
        filters: cleanFilters,
        timestamp: new Date().toISOString(),
      })

      return {
        success: true,
        message: 'Reporte abierto en una nueva pestaña',
      }
    } catch (error) {
      let errorMessage = 'Error al abrir el reporte'

      // Handle 404 (no data found)
      if (error.status === 404) {
        errorMessage = 'No se encontraron datos para los filtros especificados'
      } else if (error.status === 403) {
        errorMessage = 'No tienes permisos para generar reportes'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return {
        success: false,
        error: errorMessage,
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Update filters
   */
  setFilters({ commit }, filters) {
    commit('SET_FILTERS', filters)
  },

  /**
   * Clear all filters
   */
  clearFilters({ commit }) {
    commit('CLEAR_FILTERS')
  },

  /**
   * Clear error
   */
  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
